import { Boxes, Search, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../../lib/api'
import { formatMoney } from '../../lib/format'
import { productImageUrl } from '../../lib/images'
import type { AdminData } from '../../types/commerce'

type ProductFilter = 'todos' | 'ativos' | 'inativos' | 'sem_estoque'

export function AdminProducts() {
  const [data, setData] = useState<AdminData | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<ProductFilter>('todos')
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [notice, setNotice] = useState('')

  const removeProduct = async (productId: string, name: string) => {
    if (!window.confirm(`Excluir "${name}" do catálogo? Se houver pedidos anteriores, o produto será arquivado para preservar o histórico.`)) return
    setDeletingId(productId); setError(''); setNotice('')
    try {
      const outcome = await adminApi.deleteProduct(productId)
      setData(await adminApi.data())
      setNotice(outcome === 'deleted' ? 'Produto excluído permanentemente.' : 'Produto arquivado para preservar o histórico de pedidos.')
    } catch (err) { setError(err instanceof Error ? err.message : 'Não foi possível excluir o produto.') }
    finally { setDeletingId(null) }
  }

  useEffect(() => { adminApi.data().then(setData).catch(err => setError(err.message)) }, [])

  const stockOf = (variants: AdminData['products'][number]['variants']) => variants.filter(v => v.active).reduce((sum, v) => sum + Number(v.stock), 0)
  const products = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (data?.products ?? []).filter(({ product, variants }) => {
      const stock = stockOf(variants)
      const matchesSearch = !term || product.name.toLowerCase().includes(term) || product.slug.toLowerCase().includes(term)
      const matchesFilter = filter === 'todos' || (filter === 'ativos' && product.active) || (filter === 'inativos' && !product.active) || (filter === 'sem_estoque' && stock === 0)
      return matchesSearch && matchesFilter
    })
  }, [data, search, filter])

  const filterCounts = useMemo(() => {
    const items = data?.products ?? []
    return {
      todos: items.length,
      ativos: items.filter(({ product }) => product.active).length,
      inativos: items.filter(({ product }) => !product.active).length,
      sem_estoque: items.filter(({ variants }) => stockOf(variants) === 0).length
    }
  }, [data])

  return (
    <div className="admin-page">
      <div className="admin-page__heading"><div><span>CATÁLOGO</span><h1>Produtos</h1><p>Encontre rapidamente o que precisa editar, publicar ou repor.</p></div><Link className="button button--dark" to="/admin/produtos/novo">NOVO PRODUTO</Link></div>
      {error && <div className="form-error">{error}</div>}
      {notice && <div className="form-success">{notice}</div>}
      {!data && !error ? <div className="admin-loading">Carregando catálogo...</div> : <section className="admin-panel">
        <div className="admin-toolbar admin-toolbar--smart">
          <label className="admin-search"><Search size={17} /><input aria-label="Buscar produtos" placeholder="Buscar por nome ou slug" value={search} onChange={e => setSearch(e.target.value)} /></label>
          <div className="admin-filter-tabs" aria-label="Filtrar produtos">
            {([
              ['todos', 'Todos'],
              ['ativos', 'Ativos'],
              ['inativos', 'Inativos'],
              ['sem_estoque', 'Sem estoque']
            ] as const).map(([value, label]) => <button key={value} className={filter === value ? 'is-active' : ''} onClick={() => setFilter(value)} aria-pressed={filter === value}>{label}<span>{filterCounts[value]}</span></button>)}
          </div>
        </div>

        {products.length === 0 ? <div className="admin-empty"><Boxes size={24} /><strong>{data?.products.length === 0 ? 'Seu catálogo ainda está vazio' : 'Nenhum produto encontrado'}</strong><p>{data?.products.length === 0 ? 'Cadastre o primeiro produto para começar a montar a loja.' : 'Tente outro termo de busca ou remova os filtros.'}</p>{data?.products.length === 0 && <Link className="button button--dark button--small" to="/admin/produtos/novo">CADASTRAR PRIMEIRO PRODUTO</Link>}</div> : <div className="admin-table-wrap"><table><thead><tr><th>Produto</th><th>Preço</th><th>Estoque</th><th>Status</th><th></th></tr></thead><tbody>{products.map(({ product, images, variants }) => { const stock = stockOf(variants); return <tr key={product.id}><td><div className="product-cell">{images[0]?.storage_path ? <img src={productImageUrl(images[0].storage_path)} alt="" /> : <div className="mini-placeholder">GK</div>}<div><strong>{product.name}</strong><span>{product.slug}</span></div></div></td><td>{product.sale_price != null ? formatMoney(product.sale_price) : formatMoney(product.price)}</td><td><strong className={stock === 0 ? 'admin-stock-zero' : undefined}>{stock}</strong>{stock === 0 && <span className="admin-cell-note">sem estoque</span>}</td><td><span className={`status-pill ${product.active ? 'status-confirmado' : 'status-cancelado'}`}>{product.active ? 'ativo' : 'inativo'}</span></td><td><div className="admin-product-actions"><Link className="text-link" to={`/admin/produtos/${product.id}`}>EDITAR</Link><button type="button" className="text-link" disabled={deletingId !== null} onClick={() => void removeProduct(product.id, product.name)} aria-label={`Excluir ${product.name}`}><Trash2 size={15} /> {deletingId === product.id ? "EXCLUINDO..." : "EXCLUIR"}</button></div></td></tr> })}</tbody></table></div>}
      </section>}
    </div>
  )
}
